import { useEffect, useState } from 'react';
import { Box, Typography, Chip, Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { fmtSize } from './constants';
import {
    listProviderFiles, uploadProviderFiles, downloadProviderFileUrl, deleteProviderFile, currentUserName,
} from '../../../services/integrationHubService';

const BLUE = '#004df2';

// Files held against one PROVIDER — shared across every client that has it —
// read live from blob storage, no SQL row of its own.

const Attachments = ({ providerId }) => {
    const [files, setFiles] = useState(null); // null = not loaded yet
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        listProviderFiles(providerId)
            .then((f) => { if (alive) setFiles(f); })
            .catch((e) => { if (alive) setError(e.message); });
        return () => { alive = false; };
    }, [providerId]);

    const upload = async (fileList) => {
        const picked = Array.from(fileList || []);
        if (picked.length === 0) return;
        setBusy(true);
        setError(null);
        try {
            const who = await currentUserName();
            setFiles(await uploadProviderFiles(providerId, picked, who));
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    // Opens in a new tab rather than forcing a save — the browser renders
    // images/PDFs/text inline, and the reader can still use its own
    // download/save affordance for anything it can't render. The tab is
    // opened synchronously (before the auth'd fetch resolves) so popup
    // blockers don't treat the later redirect as an unrequested popup.
    const preview = async (filename) => {
        const win = window.open('', '_blank');
        try {
            const url = await downloadProviderFileUrl(providerId, filename);
            if (win) win.location = url;
        } catch (e) {
            if (win) win.close();
            setError(e.message);
        }
    };

    const remove = async (filename) => {
        setBusy(true);
        setError(null);
        try {
            setFiles(await deleteProviderFile(providerId, filename));
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.75, letterSpacing: 0.3 }}>FILES</Typography>
            {error && <Typography sx={{ color: '#c62828', fontSize: 12.5, mb: 0.5 }}>{error}</Typography>}
            {(files || []).length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {files.map((a) => (
                        <Chip
                            key={a.filename}
                            variant="outlined"
                            icon={<AttachFileIcon sx={{ fontSize: 15 }} />}
                            label={`${a.filename}${a.size ? ` · ${fmtSize(a.size)}` : ''}`}
                            onClick={() => preview(a.filename)}
                            onDelete={() => remove(a.filename)}
                            deleteIcon={busy ? undefined : <DeleteIcon sx={{ fontSize: 15 }} />}
                            disabled={busy}
                            sx={{ borderRadius: 2, fontSize: 12, maxWidth: 260 }}
                        />
                    ))}
                </Stack>
            )}
            <Button component="label" size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} disabled={busy}
                sx={{ textTransform: 'none', color: BLUE, fontSize: 12.5 }}>
                {busy ? 'Uploading…' : 'Add file'}
                <input type="file" hidden multiple onChange={(e) => upload(e.target.files)} />
            </Button>
        </Box>
    );
};

export default Attachments;
